// src/services/optilogicService.js

// Store the API key in memory for runtime usage
let userAppKey = sessionStorage.getItem("optilogicToken") || "";

// Login function
export const login = async (appKey) => {
  try {
    const response = await fetch("https://api.optilogic.app/v0/account", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-APP-KEY": appKey,
      },
    });

    if (!response.ok) {
      console.error(`HTTP Error: ${response.status}`);
      return { success: false, error: `HTTP error: ${response.status}` };
    }

    const data = await response.json();

    if (data.result === "error") {
      console.error("API Error:", data.error);
      return { success: false, error: data.error || "Unknown error occurred" };
    }

    if (data.result === "success") {
      userAppKey = appKey;
      sessionStorage.setItem("optilogicToken", appKey); // Store token
      sessionStorage.setItem("optilogicUser", JSON.stringify(data)); // Store user details
      return { success: true, data: data };
    }

    return { success: false, error: "Unexpected response format" };
  } catch (error) {
    console.error("Error during login:", error.message);
    return { success: false, error: error.message };
  }
};

// Logout function
export const logout = () => {
  sessionStorage.removeItem("optilogicToken");
  sessionStorage.removeItem("optilogicUser");
  userAppKey = ""; // Clear in-memory token
};

// Check if authenticated
export const isAuthenticated = () => {
  return !!sessionStorage.getItem("optilogicToken");
};

export const getUserName = () => {
  const userData = sessionStorage.getItem("optilogicUser");
  if (userData) {
    try {
      const user = JSON.parse(userData);
      return user.name || ""; // Set the user's name
    } catch (e) {
      console.error("Error parsing user data:", e.message);
      return "";
    }
  }
};

// Getter for userAppKey
export const getUserAppKey = () => {
  // Always sync with sessionStorage to handle page reloads
  if (!userAppKey && sessionStorage.getItem("optilogicToken")) {
    userAppKey = sessionStorage.getItem("optilogicToken");
  }
  return userAppKey;
};

// Example API call function
export const storages = async () => {
  try {
    const response = await fetch("https://api.optilogic.app/v0/storages", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-APP-KEY": userAppKey, // Use in-memory key
      },
    });

    if (!response.ok) {
      console.error(`HTTP Error: ${response.status}`);
      return { success: false, error: `HTTP error: ${response.status}` };
    }

    const data = await response.json();

    if (data.result === "error") {
      console.error("API Error:", data.error);
      return { success: false, error: data.error || "Unknown error occurred" };
    }

    return { success: true, data: data };
  } catch (error) {
    console.error("Error during operation:", error.message);
    return { success: false, error: error.message };
  }
};

export const modelList = async () => {
  const rawList = await storages(); // Get data from storages API
  console.log("rawList:", rawList); // Debug log to inspect the API response

  // Check if the API call was successful and contains data
  if (!rawList.success || !rawList.data || !rawList.data.storages) {
    console.error("Error fetching models or invalid response format.");
    return [];
  }

  // Filter the storages for models with type "postgres_db"
  const currModelList = rawList.data.storages; // This should be an array
  const filteredModels = currModelList.filter(
    (model) => model.type === "postgres_db"
  );

  console.log("Filtered Models:", filteredModels); // Debug log to verify output
  return filteredModels; // Return filtered list
};

export const tableList = async (modelName) => {
  const response = await fetch(
    `https://api.optilogic.app/v0/storage/${modelName}/tables?rowCounts=true`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-APP-KEY": userAppKey, // Use in-memory key
      },
    }
  );

  if (!response.ok) {
    console.error(`HTTP Error: ${response.status}`);
    return [];
  }

  const data = await response.json();

  const default_schema = data.schemas.filter((s) => s.is_default_schema);
  console.log("default schema: ", default_schema);

  const table_list = data.tables.filter(
    (t) => t.schema === default_schema[0].name
  );

  return table_list;
};

export const getTableData = async (modelName, tableName) => {
  const r_url = `https://api-dev.cosmicfrog.optilogic.app/datacrud/cosmicfrog/v0.2/anura/${modelName}/${tableName}/grid-data?per=0&returnInvalidColumnsOnly=false`;
  const r_headers = {
    "Content-Type": "application/json",
    Connection: "keep-alive",
    "X-APP-KEY": userAppKey, // Use in-memory key
  };
  const r_data = {
    startRow: 0,
    endRow: 100,
    rowGroupCols: [],
    valueCols: [],
    pivotCols: [],
    pivotMode: false,
    groupKeys: [],
    filterModel: {},
    sortModel: [],
  };

  const response = await fetch(r_url, {
    method: "POST", // Set the HTTP method
    headers: r_headers, // Add headers
    body: JSON.stringify(r_data), // Stringify the data
  });

  if (!response.ok) {
    console.error(`HTTP Error: ${response.status}`);
    return [];
  }

  // response comes back as
  // { "data" : [{id: xx, column1: 00,..}, {id: xx, column1: 00,..}],
  //   "rows_number": 2,
  //   "count": 2,
  //   "pivotFields" null
  //  }

  const data = response.json();

  return data;
};

export const writeTableValue = async (modelName, tableName, data) => {
  const r_url = `https://api-dev.cosmicfrog.optilogic.app/datacrud/cosmicfrog/v0.2/anura/${modelName}/${tableName}`;
  const r_headers = {
    "Content-Type": "application/json",
    Connection: "keep-alive",
    "X-APP-KEY": userAppKey, // Use in-memory key
  };

  const response = await fetch(r_url, {
    method: "PUT", // Set the HTTP method
    headers: r_headers, // Add headers
    body: JSON.stringify(data), // Stringify the data
  });

  if (!response.ok) {
    console.error(`HTTP Error: ${response.status}`);
    return "Error";
  }
};

export const geocodeModelTable = async (
  model,
  table,
  geocode_provider,
  geocode_key
) => {
  const r_url = `https://api-dev.cosmicfrog.optilogic.app/hypnotoad/cosmicfrog/v0.2/geocoding/geocode/table?model_name=${model}&table_name=${table}&ignore_low_confidence=true&geoprovider=${geocode_provider}&geo_api_key=${geocode_key}`;
  const r_headers = {
    "Content-Type": "application/json",
    Connection: "keep-alive",
    "X-APP-KEY": userAppKey, // Use in-memory key
  };

  const response = await fetch(r_url, {
    method: "POST", // Set the HTTP method
    headers: r_headers, // Add headers
  });

  return response.text();
};

export const geocodeLocationData = async (
  geodata,
  geocode_provider,
  geocode_key
) => {
  const r_url = `https://api-dev.cosmicfrog.optilogic.app/hypnotoad/cosmicfrog/v0.2/geocoding/geocode/location?geoprovider=${geocode_provider}&geoapikey=${geocode_key}`;
  const r_headers = {
    "Content-Type": "application/json",
    Connection: "keep-alive",
    "X-APP-KEY": userAppKey, // Use in-memory key
  };

  const response = await fetch(r_url, {
    method: "POST", // Set the HTTP method
    headers: r_headers, // Add headers
    body: JSON.stringify(geodata), // Stringify the data
  });

  if (!response.ok) {
    console.error(`HTTP Error: ${response.status}`);
    return [];
  }
  /*
  response looks like
  [
  [
    {
      "id": "home",
      "placename": "634 S Main St, Chelsea, MI 48118",
      "latitude": 42.312343,
      "longitude": -84.020807,
      "resulttype": "best",
      "geoProviderInfo": "High"
    }
  ],
  [
    {
      "id": "parents",
      "placename": "70 Grassland Rd, Milford, CT 06461",
      "latitude": 41.240632,
      "longitude": -73.063641,
      "resulttype": "best",
      "geoProviderInfo": "High"
    }
  ]
]

  */

  const data = response.json();

  return data;
};

// Get DataStar projects
export const getDataStarProjects = async () => {
  const appKey = getUserAppKey();
  const response = await fetch("https://api.optilogic.app/v0/datastar/projects", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-APP-KEY": appKey,
    },
  });

  if (!response.ok) {
    console.error(`HTTP Error: ${response.status}`);
    return [];
  }

  const data = await response.json();

  return data.storages;
};

// Get macros for a DataStar project
export const getDataStarProjectMacros = async (projectId) => {
  const appKey = getUserAppKey();
  const response = await fetch(`https://api.optilogic.app/v0/datastar/projects/${projectId}/macros`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-APP-KEY": appKey,
    },
  });

  if (!response.ok) {
    console.error(`HTTP Error: ${response.status}`);
    return [];
  }

  const data = await response.json();

  return data.items;
};

export const setSelectedModel = (selectedModel) => {
  console.log("Selected Model:", selectedModel);
  sessionStorage.setItem('ol_selectedModel', JSON.stringify(selectedModel));
}

export const getSelectedModel = () => {
  return JSON.parse(sessionStorage.getItem('ol_selectedModel')) || "";
}

/**
 * List files in workspace matching a filter pattern
 * @param {string} workspace - Workspace name (e.g., "Studio")
 * @param {string} filterPattern - Regex filter pattern (default: .*\.py$ for Python files)
 * @returns {Promise<Array>} Array of file objects with filename, directoryPath, filePath, contentLength
 */
export const listFiles = async (workspace, filterPattern = '.*\\.py$') => {
  const appKey = getUserAppKey();

  if (!appKey) {
    throw new Error('Not authenticated');
  }

  const url = `https://api.optilogic.app/v0/${workspace}/files?filter=${encodeURIComponent(filterPattern)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'X-APP-KEY': appKey
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to list files: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.result === 'success') {
    return data.files || [];
  } else {
    throw new Error('Failed to retrieve file list');
  }
}

/**
 * Get a single cell value from a table
 * @param {string} modelName - Model name
 * @param {string} tableName - Table name
 * @param {string} id - Row ID
 * @param {string} column - Column name
 * @returns {Promise<any>} The cell value
 */
export const getCellData = async (modelName, tableName, id, column) => {
  // TODO: Implement API call to fetch single cell data
  // This will need to query the table for the specific row by ID
  // and return the value of the specified column

  // Placeholder implementation:
  // Option 1: Fetch the entire row and extract the column value
  // Option 2: If API supports, fetch just the specific cell

  try {
    const r_url = `https://api-dev.cosmicfrog.optilogic.app/datacrud/cosmicfrog/v0.2/anura/${modelName}/${tableName}/grid-data?per=0&returnInvalidColumnsOnly=false`;
    const r_headers = {
      "Content-Type": "application/json",
      Connection: "keep-alive",
      "X-APP-KEY": userAppKey,
    };
    const r_data = {
      startRow: 0,
      endRow: 100,
      columns: ["id", column],
      rowGroupCols: [],
      valueCols: [],
      pivotCols: [],
      pivotMode: false,
      groupKeys: [],
      filterModel: {
        id: {
          filterType: "number",
          type: "equals",
          filter: id
        }
      },
      sortModel: [],
    };

    const response = await fetch(r_url, {
      method: "POST",
      headers: r_headers,
      body: JSON.stringify(r_data),
    });

    if (!response.ok) {
      console.error(`HTTP Error: ${response.status}`);
      return null;
    }

    const data = await response.json();

    // Extract the specific cell value
    if (data.data && data.data.length > 0) {
      const row = data.data[0];
      return row[column] !== undefined ? row[column] : null;
    }

    return null;
  } catch (error) {
    console.error("Error fetching cell data:", error);
    return null;
  }
}